import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { CircularProgress, Tooltip, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import PaymentAPI from 'api/faber/PaymentAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import { IFilterField } from 'common/AbstractDataGridToolbar/CustomDataGridFilter'
import { Toolbar } from 'common/AbstractDataGridToolbar/CustomToolbar'
import AbstractDataGridTooltip from 'common/AbstractDataGridTooltip'
import format from 'date-fns/format'
// TO DO : IMPLEMENT WHEN DATE FILTER IS UP
//import { changeMonth } from 'helpers/date'
import OrbitSession from 'hooks/useCookies'
import { RefundRequestsType } from 'types/api/faber/payment.type'

import FaberRefundMenu from './FaberRefundMenu'

export default function FaberRefundRequests() {
  const [refundStatusChecked, setRefundStatusChecked] = useState<string[]>([])
  // TO DO : IMPLEMENT WHEN DATE FILTER IS UP
  // const [startDate, setStartDate] = useState<Date>(changeMonth(new Date(), -3))
  // const [endDate, setEndDate] = useState<Date>(changeMonth(new Date(), +3))
  const [rows, setRows] = useState<RefundRequestsType[]>([])
  const [filterActive, setFilterActive] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [tempSearchValue, setTempSearchValue] = useState<string>('')
  const [searchValue, setSearchValue] = useState<string>('')
  const [searchCriteria, setSearchCriteria] = useState<string>('Email')
  const [pageOffset, setPageOffset] = useState(0)
  const [pageSize] = useState(10)
  const [page, setPage] = useState(0)
  const [rowCount, setRowCount] = useState(0)

  const role = OrbitSession.get('role')
  const faberToken = OrbitSession.get('faberToken')

  const searchOptions = ['Email', 'MSISDN', 'ICCID', 'Customer ID']

  const onSearchValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setTempSearchValue(e.target.value.replace('+', '%2B'))
  }

  const onSearchSubmit = () => {
    setSearchValue(tempSearchValue)
    resetPage()
  }

  const handleSearchCriteriaChange = (e, v: string) => {
    setSearchCriteria(v)
  }
  // TO DO : IMPLEMENT WHEN DATE FILTER IS UP
  // useEffect(() => {
  //   resetPage()
  // }, [startDate, endDate])

  const queryOptions = React.useMemo(
    () => ({
      pageOffset,
      pageSize,
      refundStatusChecked,
      searchCriteria,
      searchValue
    }),
    [pageOffset, pageSize, refundStatusChecked, searchCriteria, searchValue]
  )

  const {
    data: getAllCustomerPayments,
    isLoading: gettingAllCustomerPayments,
    refetch: refetchRefundRequests
  } = useQuery(
    ['getAllRefundRequests', [queryOptions]],
    async () => {
      const {
        pageOffset,
        pageSize,
        refundStatusChecked,
        searchCriteria,
        searchValue
      } = queryOptions
      return PaymentAPI.getAllRefundRequests(
        pageOffset,
        pageSize,
        faberToken,
        refundStatusChecked,
        searchCriteria,
        searchValue
      )
    },
    {
      onError: () => {
        toast.error('Error getting payment details')
        setRows([])
        setIsLoading(false)
      }
    }
  )

  const handlePageChange = (newPage: number) => {
    const tmpOffset = newPage * pageSize
    setPage(newPage)
    setPageOffset(tmpOffset)
  }

  useEffect(() => {
    if (getAllCustomerPayments) {
      const { data: allSubscriptions, total } = getAllCustomerPayments.data.body
      if (allSubscriptions && allSubscriptions.length > 0) {
        setRows(allSubscriptions)
      }
      setIsLoading(false)
      setRowCount(total)
    } else {
      setRows([])
      setIsLoading(false)
    }
  }, [getAllCustomerPayments])

  function resetPage() {
    setPageOffset(0)
    setPage(0)
  }

  const updateRow = () => {
    refetchRefundRequests()
  }

  const columns: GridColDef[] = [
    {
      sortable: true,
      field: 'CreatedAt',
      headerName: 'Request Created At',
      flex: 1,
      minWidth: 100,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: { row: RefundRequestsType }) => {
        return (
          <Box sx={{ fontSize: '12px' }}>
            {format(new Date(params.row.CreatedAt), 'dd MMM yyyy HH:mm')}
          </Box>
        )
      }
    },

    {
      sortable: false,
      field: 'firstName',
      headerName: 'Customer Name',
      flex: 1.25,
      minWidth: 100,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: { row: RefundRequestsType }) => {
        return (
          <AbstractDataGridTooltip>
            {params.row.firstName} {params.row.lastName}
          </AbstractDataGridTooltip>
        )
      }
    },
    {
      sortable: false,
      field: 'msisdn',
      headerName: 'MSISDN',
      flex: 1,
      minWidth: 100,
      filterable: false,
      disableColumnMenu: true
    },
    {
      sortable: false,
      field: 'iccid',
      headerName: 'ICCID',
      flex: 1,
      minWidth: 100,
      filterable: false,
      disableColumnMenu: true
    },
    {
      sortable: false,
      field: 'userId',
      headerName: 'Customer ID',
      flex: 1.5,
      minWidth: 100,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: { row: RefundRequestsType }) => {
        return (
          <Tooltip title={params.row.userId} placement='top'>
            <Typography sx={{ fontSize: '12px', color: '#253140' }}>
              {params.row.userId}
            </Typography>
          </Tooltip>
        )
      }
    },
    {
      sortable: false,
      field: 'email',
      headerName: 'Customer Email',
      flex: 2,
      minWidth: 100,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: { row: RefundRequestsType }) => {
        return (
          <Tooltip title={params.row.email} placement='top'>
            <Typography sx={{ fontSize: '12px', color: '#253140' }}>
              {params.row.email}
            </Typography>
          </Tooltip>
        )
      }
    },
    {
      sortable: false,
      field: 'refundStatus',
      headerName: 'Refund Status',
      flex: 0.75,
      minWidth: 50,
      filterable: true,
      renderCell: (params: { row: RefundRequestsType }) => {
        return (
          <Typography
            sx={{
              color: params.row.refundStatus === 'PENDING' ? 'red' : 'black',
              fontSize: '12px'
            }}
          >
            {params.row.refundStatus}
          </Typography>
        )
      }
    },
    {
      sortable: false,
      field: 'actions',
      headerName: 'Actions',
      renderCell: (params: { row: RefundRequestsType }) => {
        return (
          <FaberRefundMenu
            paymentRow={params.row}
            updateRow={updateRow}
            role={role}
          />
        )
      },
      flex: 0.5,
      minWidth: 50,
      filterable: false,
      disableColumnMenu: true
    }
  ]

  const filterFields = useMemo(() => {
    let tempFilterFields: IFilterField[] = []
    tempFilterFields = [
      {
        values: ['SUCCESS', 'PENDING'],
        checked: refundStatusChecked,
        setChecked: setRefundStatusChecked,
        name: 'Refund Status',
        index: 'refundStatus'
      }
    ]
    return tempFilterFields
  }, [refundStatusChecked])
  4
  return !isLoading ? (
    <MUIDataGrid
      getRowId={(row: RefundRequestsType) =>
        row.ID.toString() + rows.indexOf(row).toString()
      }
      tableHeight='750px'
      columns={columns}
      rows={rows}
      pagination
      paginationMode='server'
      rowCount={rowCount}
      page={page}
      pageSize={pageSize}
      onPageChange={handlePageChange}
      loading={gettingAllCustomerPayments}
      disableColumnMenu={false}
      disableColumnSelector={true}
      sortingMode='server'
      sortingOrder={['desc', 'asc']}
      components={{
        Toolbar: Toolbar
      }}
      componentsProps={{
        toolbar: {
          serverFilterProps: {
            filterFields,
            filterActive,
            setFilterActive
          },
          searchBar: 'server',
          serverSearchBarProps: {
            onSubmit: onSearchSubmit,
            onChange: onSearchValueChange,
            options: searchOptions,
            defaultValue: searchCriteria,
            handleChange: handleSearchCriteriaChange
          },
          // TO DO : HIDE FIRST, IMPLEMENT WHEN FUNCTION IS UP
          // dateRangePicker: (
          //   <DateRangePicker
          //     startDate={startDate}
          //     setStartDate={setStartDate}
          //     endDate={endDate}
          //     setEndDate={setEndDate}
          //   />
          // ),
          filter: 'server'
        }
      }}
    />
  ) : (
    <CircularProgress />
  )
}
